// src/App.js
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Components
import Layout from './components/Layout/Layout';
import AgentPanel from './components/agent/AgentPanel';
import Login from './components/Auth/Login';
import TenantSelector from './components/Auth/TenantSelector';
import TenantSignUp from './components/Auth/TenantSignUp';
import ForgotPassword from './components/Auth/ForgotPassword';
import AcceptInvite from './components/Auth/AcceptInvite';
import ChangePassword from './components/Auth/ChangePassword';
import DashboardHome from './pages/Dashboard/DashboardHome.tsx';
import DashboardTasks from './components/Dashboards/DashboardTasks';
import DashboardAlerts from './components/Dashboards/DashboardAlerts';
import LeadsPage from './components/Leads/LeadsPage';
import SchedulingDashboard from './components/Schedule/SchedulingDashboard';
import CalendarRoute from './components/Schedule/CalendarRoute';
import CommunicationsPage from './components/Communications/CommunicationsPage';
import SettingsShell from './pages/settings/v2/SettingsShell';
import PipelineView from './components/Leads/Pipeline';
import LeadWorkQueue from './components/Leads/LeadWorkQueue';
import MarketplaceView from './components/Marketplace/MarketplaceView';
import Campaigns from './components/Revival/Campaigns';
import CampaignDetail from './components/Revival/CampaignDetail';
import Planner from './components/Revival/Planner';
import History from './components/Revival/History';
import AiInsights from './components/Revival/AiInsights';
import QuoteDetail from './pages/Quotes/QuoteDetail';
import QuoteCreate from './pages/Quotes/QuoteCreate';
import RevivalScanner from './components/Revival/Scanner';
import Overview from './components/Revival/Overview';
import AnalyticsRouter from './components/Analytics/AnalyticsRouter';
import FinancePage from './components/Finnance/FinancePage';
import MarketingPage from './components/Marketing/MarketingPage';
import CustomersPage from './components/Customers/CustomersPage';
import TeamManagement from './components/Settings/TeamManagement';
import OperationsRouter from './components/Operations/OperationsRouter';
import SalesRouter from './pages/Sales/SalesRouter';
import PlatformHealth from './pages/Platform/PlatformHealth';
import PlatformBilling from './pages/Platform/PlatformBilling';
import RequireSuperuser from './components/Auth/RequireSuperuser';
import ProposalsPage from './pages/AI/ProposalsPage';
import SetupPage from './pages/Setup/SetupPage';
import ActivityFeed from './pages/AI/ActivityFeed';
import OnboardingSelf from './pages/Public/OnboardingSelf';
import DocumentRequestSelf from './pages/Public/DocumentRequestSelf';
import api from './apiClient';
import { refreshAccessToken } from './api/client';
import { ThemeProvider } from './theme/ThemeProvider';

// Helpers
import {
  clearActiveTenant,
  getActiveTenant,
  getStoredTenants,
  persistTenants,
  setActiveTenant,
} from './helpers/tenantHelpers';

// Styles
import './App.css';
import './index.css';

export const AuthContext = createContext();

function safeParse(json, fallback = null) {
  try {
    return JSON.parse(json ?? '');
  } catch {
    return fallback;
  }
}

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // False until the localStorage auth-restore effect has run once. Routing must
  // wait for this — otherwise a fresh deep-link load (e.g. /schedule/day?date=…)
  // renders with isAuthenticated still false, the unauth catch-all redirects it
  // to /login (dropping the URL), and the authed catch-all then lands /dashboard.
  const [authResolved, setAuthResolved] = useState(false);
  const [isTenantSetupComplete, setTenantSetupComplete] = useState(true);
  const [token, setToken] = useState(
    localStorage.getItem('token') || localStorage.getItem('access_token') || null
  );
  const [user, setUser] = useState(() => safeParse(localStorage.getItem('user'), null));
  const [tenant, setTenant] = useState(() => getActiveTenant() || null);
  const [tenants, setTenants] = useState(() => getStoredTenants());

  const logout = useCallback(() => {
    localStorage.clear();
    setIsAuthenticated(false);
    setTenantSetupComplete(false);
    setToken(null);
    setUser(null);
    setTenant(null);
    setTenants([]);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      try {
        const storedToken = localStorage.getItem('token');
        const fallbackToken = localStorage.getItem('access_token');
        const tokenExpiry = localStorage.getItem('token_expiry');
        const userData = safeParse(localStorage.getItem('user'), null);
        const tenantData = getActiveTenant();
        const storedTenants = getStoredTenants();
        if (!cancelled) setTenants(storedTenants);

        let accessToken = storedToken || fallbackToken;
        const expired = !tokenExpiry || new Date(tokenExpiry) <= new Date();

        // Access token expired (60-min TTL) but a refresh token is on file →
        // silently exchange it instead of logging out. The old code called
        // logout() here, which localStorage.clear()'d the still-valid 7-day
        // refresh token too, so any reload an hour after login dropped the user
        // to /login. Now the session survives for the refresh lifetime.
        if (accessToken && expired && localStorage.getItem('refresh')) {
          try {
            accessToken = await refreshAccessToken();
          } catch {
            accessToken = null; // refresh dead/expired → fall through to logout
          }
        }

        if (cancelled) return;

        if (accessToken) {
          const freshExpiry = localStorage.getItem('token_expiry');
          const valid = freshExpiry && new Date(freshExpiry) > new Date();
          if (valid) {
            setIsAuthenticated(true);
            setTenantSetupComplete(tenantData?.setupComplete ?? true);
            setToken(accessToken);
            setUser(userData);
            setTenant(tenantData);
          } else {
            logout();
          }
        }
        // No token at all → simply unauthenticated (nothing to clear).
      } catch {
        if (!cancelled) logout();
      } finally {
        // Auth state is now decided (authed or not) — routing may proceed.
        if (!cancelled) setAuthResolved(true);
      }
    };
    boot();
    return () => { cancelled = true; };
  }, [logout]);

  useEffect(() => {
    const syncTenant = () => setTenant(getActiveTenant());
    const syncTenants = () => setTenants(getStoredTenants());
    const onStorage = (e) => {
      if (e.key === 'activeTenant') syncTenant();
      if (e.key === 'tenants') syncTenants();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('activeTenant:changed', syncTenant);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('activeTenant:changed', syncTenant);
    };
  }, []);

  const login = (accessToken, refreshToken, expiry, tenantList, userData) => {
    const normalizedTenants = persistTenants(tenantList);
    clearActiveTenant();

    localStorage.setItem('token', accessToken || '');
    localStorage.setItem('access_token', accessToken || '');
    if (refreshToken) localStorage.setItem('refresh', refreshToken);
    else localStorage.removeItem('refresh');
    localStorage.setItem('token_expiry', expiry);
    localStorage.setItem('user', JSON.stringify(userData));
    // Nav role gate: the backend normalizes role (superuser -> "admin") but
    // the tenant payload carries no role key and nothing ever wrote userRole,
    // so getUserRole() fell through to "Member" for every session.
    localStorage.setItem('userRole', userData?.role || 'Member');

    const autoSelect =
      Array.isArray(normalizedTenants) && normalizedTenants.length === 1
        ? normalizedTenants[0]
        : null;
    if (autoSelect?.id) {
      const saved = setActiveTenant(autoSelect);
      setTenant(saved);
      setTenantSetupComplete(saved?.setupComplete ?? true);
    } else {
      clearActiveTenant();
      setTenant(null);
      setTenantSetupComplete(false);
    }

    setIsAuthenticated(true);
    setToken(accessToken || null);
    setUser(userData);
    setTenants(normalizedTenants);
  };

  const selectTenant = async (tenantId) => {
    const tenantList = getStoredTenants();
    const selected = tenantList.find((t) => String(t.id) === String(tenantId));
    if (!selected) throw new Error('Please choose a tenant to continue.');

    const { data } = await api.post('/accounts/auth/switch-tenant/', {
      tenant_id: tenantId,
    });

    const mergedTenant = setActiveTenant({ ...data?.activeTenant, ...selected });
    setTenant(mergedTenant);
    setTenantSetupComplete(mergedTenant?.setupComplete ?? true);
    return mergedTenant;
  };

  return {
    isAuthenticated,
    authResolved,
    login,
    logout,
    isTenantSetupComplete,
    token,
    user,
    tenant,
    tenants,
    selectTenant,
    requiresTenantSelection:
      isAuthenticated &&
      (!tenant?.id || (tenants.length > 0 && !tenants.find((t) => String(t.id) === String(tenant.id)))),
  };
};

const UnauthenticatedRoutes = () => (
  <Routes>
    <Route path="/login" element={<Login />} />
    <Route path="/signup" element={<TenantSignUp />} />
    <Route path="/forgot-password" element={<ForgotPassword />} />
    <Route path="/accept-invite" element={<AcceptInvite />} />
    <Route path="/change-password" element={<ChangePassword />} />
    {/* Employee self-serve links (token is the auth): opened from a phone, no login. */}
    <Route path="/onboarding/:token" element={<OnboardingSelf />} />
    <Route path="/docs/request/:token" element={<DocumentRequestSelf />} />
    <Route path="*" element={<Navigate to="/login" replace />} />
  </Routes>
);

const AuthenticatedApp = () => {
  const path = window.location.pathname;
  // The same self-serve links work while signed in (a manager tapping their own
  // test link, an employee who also has the app) — rendered without app chrome.
  if (/^\/onboarding\/[^/]+$/.test(path)) return <Routes><Route path="/onboarding/:token" element={<OnboardingSelf />} /></Routes>;
  if (/^\/docs\/request\/[^/]+$/.test(path)) return <Routes><Route path="/docs/request/:token" element={<DocumentRequestSelf />} /></Routes>;
  return (
    <Layout>
      <Routes>
        <Route path="/dashboard" element={<DashboardHome />} />
        {/* AGI Dashboard hidden — hardcoded mock data, will re-enable when wired to real APIs */}
        <Route path="/dashboard/tasks" element={<DashboardTasks />} />
        <Route path="/dashboard/alerts" element={<DashboardAlerts />} />
        <Route path="/customers/*" element={<CustomersPage />} />
        <Route path="/leads/pipeline" element={<PipelineView />} />
        <Route path="/leads/queue" element={<LeadWorkQueue />} />
        <Route path="/leads/under-contract" element={<MarketplaceView />} />
        <Route path="/leads/*" element={<LeadsPage />} />
        <Route path="/schedule/*" element={<SchedulingDashboard />} />

        {/* Standalone events calendar */}
        <Route path="/calendar" element={<Navigate to="/calendar/month" replace />} />
        <Route path="/calendar/month" element={<CalendarRoute view="month" />} />
        <Route path="/calendar/week" element={<CalendarRoute view="week" />} />
        <Route path="/calendar/week/:date" element={<CalendarRoute view="week" />} />
        <Route path="/calendar/day" element={<CalendarRoute view="day" />} />
        <Route path="/calendar/day/:date" element={<CalendarRoute view="day" />} />
        <Route path="/communication/*" element={<CommunicationsPage />} />
        <Route path="/communications/*" element={<CommunicationsPage />} />
        <Route path="/inbox/*" element={<Navigate to="/communication/inbox" replace />} />

        {/* Revival */}
        <Route path="/quotes/create" element={<QuoteCreate />} />
        <Route path="/quotes/:quoteId" element={<QuoteDetail />} />
        <Route path="/revival/quote/:quoteId" element={<QuoteDetail />} />
        <Route path="/revival/overview" element={<Overview />} />
        <Route path="/revival/scanner" element={<RevivalScanner />} />
        <Route path="/revival/campaigns" element={<Campaigns />} />
        <Route path="/revival/campaigns/:campaignId" element={<CampaignDetail />} />
        <Route path="/revival/planner" element={<Planner />} />
        <Route path="/revival/history" element={<History />} />
        <Route path="/revival/ai" element={<AiInsights />} />
        <Route path="/revival/opportunities" element={<AiInsights />} />
        <Route path="/revival/*" element={<Navigate to="/revival/overview" replace />} />

        {/* Finance & Marketing */}
        <Route path="/finance/*" element={<FinancePage />} />
        <Route path="/marketing/*" element={<MarketingPage />} />

        <Route path="/analytics/*" element={<AnalyticsRouter />} />
        <Route path="/operations/*" element={<OperationsRouter />} />
        <Route path="/sales/*" element={<SalesRouter />} />
        <Route path="/team" element={<TeamManagement />} />
        <Route path="/team/*" element={<TeamManagement />} />
        {/* Platform tools: superuser-only. RequireSuperuser keys off is_superuser
            (login/refresh payload, or one backend probe for pre-flag sessions);
            every /platform/* endpoint also enforces it server-side. */}
        <Route path="/platform/health" element={<RequireSuperuser><PlatformHealth /></RequireSuperuser>} />
        <Route path="/platform/billing" element={<RequireSuperuser><PlatformBilling /></RequireSuperuser>} />
        <Route path="/ai/proposals" element={<ProposalsPage />} />
        <Route path="/setup" element={<SetupPage />} />
        <Route path="/ai/activity" element={<ActivityFeed />} />
        <Route path="/ai" element={<Navigate to="/ai/proposals" replace />} />
        <Route path="/settings/:tab?/:sub?/:id?" element={<SettingsShell />} />
        <Route path="/assistant" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      <AgentPanel />
    </Layout>
  );
};

const TenantSelectionRoutes = () => {
  const navigate = useNavigate();
  const { tenants, selectTenant, logout } = useContext(AuthContext);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSelect = async (tenantId) => {
    if (!tenantId) {
      setError('Select a tenant to continue.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const active = await selectTenant(tenantId);
      const target = active?.setupComplete === false ? '/settings/team' : '/dashboard';
      navigate(target, { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.message ||
        'Unable to activate tenant. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Routes>
      <Route
        path="/select-account"
        element={
          <TenantSelector
            tenants={tenants}
            onSelect={handleSelect}
            onLogout={logout}
            loading={loading}
            error={error}
          />
        }
      />
      <Route path="*" element={<Navigate to="/select-account" replace />} />
    </Routes>
  );
};

const AppRoutes = () => {
  const { isAuthenticated, authResolved, requiresTenantSelection } = useContext(AuthContext);
  // Hold routing until auth is restored from storage, so a deep link is never
  // redirected away before we know the user is signed in.
  if (!authResolved) return <div className="app-boot-splash" aria-busy="true" />;
  if (!isAuthenticated) return <UnauthenticatedRoutes />;
  if (requiresTenantSelection) return <TenantSelectionRoutes />;
  return <AuthenticatedApp />;
};

const App = () => {
  const auth = useAuth();

  return (
    <AuthContext.Provider value={auth}>
      <ThemeProvider>
        <Router>
          {/* ⭐⭐⭐ GLOBAL MODAL ROOT — required for CustomerPopup */}
          <div id="modal-root"></div>
          <AppRoutes />
          <ToastContainer position="bottom-right" autoClose={2600} theme="light" />
        </Router>
      </ThemeProvider>
    </AuthContext.Provider>
  );
};

export default App;
